import { Box, Group, Title, Text } from '@mantine/core';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import ResponsiveRow from '@/shared/responsive/ResponsiveRow';

interface PageHeaderProps {
    title: ReactNode;
    subtitle?: ReactNode;
    /**
     * Small kicker above the title (e.g. `Behavior`, `Evidence · model`). Rendered in the
     * one canonical treatment — uppercase, letter-spaced, brand indigo. Pass a plain string.
     */
    eyebrow?: ReactNode;
    /** Optional leading icon, rendered in a muted brand tile beside the title block. */
    icon?: LucideIcon;
    /** Custom leading element — takes precedence over `icon` when both are given. */
    leading?: ReactNode;
    /** Optional extra content beneath the subtitle (e.g. a "custom view" note). */
    children?: ReactNode;
    /** Content rendered on the right of the title row (e.g. a toolbar). Stacks below the title on mobile. */
    rightToolbar?: ReactNode;
}

/**
 * The single canonical page header: an optional muted-brand icon tile beside an uppercase
 * indigo eyebrow, the title, and an optional dimmed subtitle, plus an optional right-aligned
 * action. Borderless and background-free by design — chrome stays monochrome-brand so colour
 * is reserved for data. One source of truth for the header treatment across every page.
 */
export default function PageHeaderComponent({ title, subtitle, eyebrow, icon: Icon, leading, children, rightToolbar }: PageHeaderProps): ReactElement {
    const leadingEl =
        leading ??
        (Icon && (
            <Box
                style={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--bm-color-surface)',
                    border: '1px solid var(--bm-color-border)',
                    borderRadius: 'var(--bm-radius)',
                }}
            >
                <Icon size={22} color="var(--bm-color-brand-accent)" />
            </Box>
        ));

    return (
        <Box pt="sm" pb="lg">
            <ResponsiveRow align={{ base: 'stretch', sm: 'flex-start' }} gap="md">
                <Group gap="md" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
                    {leadingEl}
                    <Box style={{ minWidth: 0 }}>
                        {eyebrow !== undefined && (
                            <Text
                                component="div"
                                size="xs"
                                fw={700}
                                tt="uppercase"
                                mb={2}
                                style={{ letterSpacing: '0.1em', color: 'var(--bm-color-brand-accent)' }}
                            >
                                {eyebrow}
                            </Text>
                        )}
                        <Title order={2} fw={700}>
                            {title}
                        </Title>
                        {subtitle !== undefined && (
                            <Text size="xs" c="dimmed" fw={500} mt={4}>
                                {subtitle}
                            </Text>
                        )}
                        {children}
                    </Box>
                </Group>
                {rightToolbar !== undefined && <Box style={{ flexShrink: 0 }}>{rightToolbar}</Box>}
            </ResponsiveRow>
        </Box>
    );
}
